import React from 'react';
import { View, StyleSheet } from 'react-native';
import { Text, TextInput } from '@/components/common/text';
import type { PaymentMethod, FeeStructure } from '@/types/residents/payment';
import type { BalanceItem } from '@/types/residents/lease';
import {
  PaymentAmountSelector,
  PaymentMethodDisplay,
  ConvenienceFeesDisplay,
} from '@/components/residents/payments/mobile/makePayment';
import { ChargeBreakdown } from '@/features/payments/components/shared/ChargeBreakdown';
import { formatCurrency } from '@/utils/common/formatting';
import { useMarketplaceColors } from '@/constants/residents/marketplaceColors';

interface PaymentDetailsContentProps {
  balanceDue: number;
  balanceItems?: BalanceItem[];
  accountCredit?: number;
  selectedAmountOption: 'balance' | 'other';
  customAmount: string;
  onSelectBalance: () => void;
  onSelectOther: () => void;
  onCustomAmountChange: (value: string) => void;
  onInfoPress: () => void;
  residentName?: string;
  disabled: boolean;
  amountError?: string;
  paymentMethod: PaymentMethod | null;
  onAddPaymentMethod: () => void;
  onChangePaymentMethod: () => void;
  methodError?: string;
  feeStructure: FeeStructure;
  inputRef?: React.RefObject<TextInput | null>;
  /** Wrapper of the amount section, so the host can scroll its error into view. */
  amountSectionRef?: React.Ref<View>;
  /** Wrapper of the payment method section, so the host can scroll its error into view. */
  methodSectionRef?: React.Ref<View>;
}

/**
 * Payment Details Content - First screen in payment flow
 * Shows itemized charges, amount selection, and payment method display
 */
export const PaymentDetailsContent: React.FC<PaymentDetailsContentProps> = ({
  balanceDue,
  balanceItems,
  accountCredit = 0,
  selectedAmountOption,
  customAmount,
  onSelectBalance,
  onSelectOther,
  onCustomAmountChange,
  onInfoPress,
  residentName,
  disabled,
  amountError,
  paymentMethod,
  onAddPaymentMethod,
  onChangePaymentMethod,
  methodError,
  feeStructure,
  inputRef,
  amountSectionRef,
  methodSectionRef,
}) => {
  const MARKETPLACE_COLORS = useMarketplaceColors();
  return (
    <View style={styles.container}>
      <View style={styles.content}>
        <ChargeBreakdown
          balanceItems={balanceItems || []}
          balanceDue={balanceDue}
          accountCredit={accountCredit}
          variant="marketplace"
        />

        {/* Account credit info line */}
        {accountCredit > 0 && (
          <Text style={[styles.creditInfo, { color: MARKETPLACE_COLORS.textMuted }]}>
            {balanceDue > 0
              ? `You have ${formatCurrency(accountCredit)} in account credit. It will apply toward future charges.`
              : `You have ${formatCurrency(accountCredit)} in account credit. This payment will add to your credit.`}
          </Text>
        )}

        {/* Payment Amount Section */}
        <View ref={amountSectionRef} style={styles.section}>
          <PaymentAmountSelector
            balanceDue={balanceDue}
            selectedOption={selectedAmountOption}
            customAmount={customAmount}
            onSelectBalance={onSelectBalance}
            onSelectOther={onSelectOther}
            onCustomAmountChange={onCustomAmountChange}
            onInfoPress={onInfoPress}
            residentName={residentName}
            disabled={disabled}
            error={amountError}
            inputRef={inputRef}
          />
        </View>

        {/* Payment Method Section */}
        <View ref={methodSectionRef} style={styles.section}>
          <PaymentMethodDisplay
            paymentMethod={paymentMethod}
            onAddPress={onAddPaymentMethod}
            onChangePress={onChangePaymentMethod}
            showExpiredError={paymentMethod?.isExpired}
            error={methodError}
          />
        </View>
      </View>

      {/* Convenience Fees Section */}
      <ConvenienceFeesDisplay feeStructure={feeStructure} />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    paddingHorizontal: 24,
    paddingVertical: 24,
    gap: 16,
  },
  section: {
    gap: 16,
  },
  creditInfo: {
    fontSize: 12,
    fontWeight: '400',
  },
});

export default PaymentDetailsContent;

